import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  PauseCircle as PauseIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ClientPaymentService from '../../services/clientPayment.service';
import { useTranslation } from '../../hooks/useTranslation';

const statusColors = {
  Active: 'success',
  suspended: 'warning',
  ended: 'error',
};

const ClientPaymentList = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [clientPayments, setClientPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchClientPayments();
  }, []);

  const fetchClientPayments = async () => {
    setLoading(true);
    try {
      const response = await ClientPaymentService.getAll();
      setClientPayments(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching client payments:', error);
      showSnackbar(t('clientPayments.errorLoading'), 'error');
      setClientPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendToggle = async (cp) => {
    try {
      const newStatus = cp.status === 'Active' ? 'suspended' : 'Active';
      await ClientPaymentService.update(cp.id, { status: newStatus });
      showSnackbar(
        newStatus === 'Active'
          ? t('clientPayments.reactivatedSuccess')
          : t('clientPayments.suspendedSuccess')
      );
      fetchClientPayments();
    } catch (error) {
      showSnackbar(t('clientPayments.errorUpdating'), 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredPayments = clientPayments.filter((cp) => {
    const term = searchTerm.toLowerCase();
    const clientName = cp.client?.name?.toLowerCase() || '';
    const paymentTypeName = cp.payment_type?.name?.toLowerCase() || '';
    return clientName.includes(term) || paymentTypeName.includes(term);
  });

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
            <Typography variant="h5" fontWeight={600}>
              {t('clientPayments.title')}
            </Typography>
            <Box display="flex" gap={1}>
              {hasPermission('clientpayments.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/memberships/assign')}
                >
                  {t('clientPayments.assignNew')}
                </Button>
              )}
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            {t('clientPayments.subtitle')}
          </Typography>

          <TextField
            placeholder={t('clientPayments.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ mb: 2, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.client')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.paymentType')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.discount')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.startDate')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.endDate')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('clientPayments.nextPayment')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : filteredPayments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {t('clientPayments.noRecordsFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayments.map((cp) => (
                    <TableRow key={cp.id} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{cp.client?.name || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={cp.payment_type?.name || '—'}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {cp.descount ? (
                          <Chip
                            label={
                              cp.descount.porcentaje
                                ? `${cp.descount.porcentaje}%`
                                : cp.descount.monto
                                  ? `$${cp.descount.monto}`
                                  : '—'
                            }
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(cp.start)}</TableCell>
                      <TableCell>{formatDate(cp.end_date)}</TableCell>
                      <TableCell>{formatDate(cp.next_payment_date)}</TableCell>
                      <TableCell>
                        <Chip
                          label={t(`clientPayments.status${cp.status?.charAt(0).toUpperCase() + cp.status?.slice(1)}`)}
                          size="small"
                          color={statusColors[cp.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={t('common.view')}>
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/memberships/${cp.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {hasPermission('clientpayments.create') && cp.status !== 'ended' && (
                            <Tooltip
                              title={
                                cp.status === 'Active'
                                  ? t('clientPayments.suspend')
                                  : t('clientPayments.reactivate')
                              }
                            >
                              <IconButton
                                size="small"
                                color={cp.status === 'Active' ? 'warning' : 'success'}
                                onClick={() => handleSuspendToggle(cp)}
                              >
                                {cp.status === 'Active' ? (
                                  <PauseIcon fontSize="small" />
                                ) : (
                                  <PlayIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientPaymentList;
